import React, { useEffect, useState, useCallback } from "react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, dateTimeFmt, todayISO, numberFmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Printer, Wallet, ShoppingBag } from "lucide-react";

export default function CashierReport() {
  const { user } = useAuth();
  const { effectiveBranch } = useBranch();
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    client.get("/reports/sales", { params: { ...(effectiveBranch ? { branch_id: effectiveBranch } : {}), start, end } })
      .then((r) => setData(r.data));
  }, [effectiveBranch, start, end]);
  useEffect(() => { load(); }, [load]);

  const quick = (days) => {
    const d = new Date(); d.setDate(d.getDate() - days);
    setStart(d.toISOString().slice(0, 10)); setEnd(todayISO());
  };

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Laporan Penjualan Kasir</h1>
          <p className="text-sm text-muted-foreground">Total uang & transaksi — harian atau rentang custom</p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-cashier-report"><Printer className="mr-2 h-4 w-4" /> Cetak Laporan</Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <div className="space-y-1"><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="cr-start" /></div>
        <div className="space-y-1"><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="cr-end" /></div>
        <Button variant="outline" size="sm" onClick={() => quick(0)}>Hari Ini</Button>
        <Button variant="outline" size="sm" onClick={() => quick(6)}>7 Hari</Button>
        <Button variant="outline" size="sm" onClick={() => quick(29)}>30 Hari</Button>
      </div>

      <div id="receipt-print" className="space-y-5 print:block">
        <div className="hidden print:block">
          <div className="text-xl font-bold">TOKO KU — Laporan Penjualan Kasir</div>
          <div className="text-sm">Kasir: {user.name} · Periode: {start} s/d {end}</div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Card className="p-5"><div className="flex items-center gap-2 text-sm text-muted-foreground"><Wallet className="h-4 w-4" /> Total Penjualan</div><div className="mt-1 font-mono text-2xl font-bold text-primary" data-testid="cr-total">{rupiah(data?.revenue)}</div></Card>
          <Card className="p-5"><div className="flex items-center gap-2 text-sm text-muted-foreground"><ShoppingBag className="h-4 w-4" /> Jumlah Transaksi</div><div className="mt-1 font-mono text-2xl font-bold">{numberFmt(data?.count || 0)}</div></Card>
        </div>

        <div className="rounded-lg border bg-white">
          <Table>
            <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Waktu</TableHead><TableHead>Kasir</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {data?.sales?.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-mono text-xs">{s.number}</TableCell>
                  <TableCell className="text-xs">{dateTimeFmt(s.created_at)}</TableCell>
                  <TableCell className="text-sm">{s.cashier_name}</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(s.total)}</TableCell>
                </TableRow>
              ))}
              {data && !data.sales.length && <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground">Tidak ada penjualan pada periode ini</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
